import {
  computeCreditsSummary,
  makeCreditsRefKey,
  readCreditsStore,
  resetCredits,
  resolveCreditsScopeKey,
  seedCreditsIfEmpty,
  updateCreditsStore,
} from '../utils/creditsStore';

function matchesMetadata(entry, match) {
  if (!entry?.metadata || !match) return false;
  for (const [k, v] of Object.entries(match)) {
    if (entry.metadata?.[k] !== v) return false;
  }
  return true;
}

export const creditsApi = {
  resolveScopeKey(companyId) {
    return resolveCreditsScopeKey(companyId);
  },

  seedIfEmpty({ companyId, total = 30 } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    return computeCreditsSummary(seedCreditsIfEmpty(scopeKey, { total }));
  },

  reset({ companyId, total = 30 } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    return computeCreditsSummary(resetCredits(scopeKey, { total }));
  },

  getSummary({ companyId } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    const store = seedCreditsIfEmpty(scopeKey, { total: 30 });
    return computeCreditsSummary(store);
  },

  previewBlock({ companyId, count } = {}) {
    const summary = this.getSummary({ companyId });
    const required = Math.max(0, Number(count || 0));
    return {
      ...summary,
      required,
      canBlock: summary.available >= required,
    };
  },

  block({ companyId, refKey, count = 1, metadata } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    const required = Math.max(0, Number(count || 0));
    if (!refKey || !required) return { ok: false, reason: 'invalid' };

    let result = { ok: true, already: false, summary: null };

    updateCreditsStore(scopeKey, (current) => {
      const summary = computeCreditsSummary(current);
      if (summary.blocks[refKey]) {
        result = { ok: true, already: true, summary };
        return current;
      }
      if (summary.available < required) {
        result = { ok: false, reason: 'insufficient', summary };
        return current;
      }

      const next = {
        ...current,
        blocks: {
          ...summary.blocks,
          [refKey]: {
            refKey,
            count: required,
            status: 'blocked',
            createdAt: new Date().toISOString(),
            metadata: metadata || {},
          },
        },
      };

      result = { ok: true, already: false, summary: computeCreditsSummary(next) };
      return next;
    });

    return result;
  },

  release({ companyId, refKey } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    if (!refKey) return { ok: false, reason: 'invalid' };

    updateCreditsStore(scopeKey, (current) => {
      const summary = computeCreditsSummary(current);
      const existing = summary.blocks[refKey];
      if (!existing) return current;
      if (existing.status !== 'blocked') return current;

      const nextBlocks = { ...summary.blocks };
      delete nextBlocks[refKey];

      return { ...current, blocks: nextBlocks };
    });

    return { ok: true };
  },

  releaseBlockedMatching({ companyId, match } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    updateCreditsStore(scopeKey, (current) => {
      const summary = computeCreditsSummary(current);
      const nextBlocks = { ...summary.blocks };
      let changed = false;

      for (const [k, entry] of Object.entries(summary.blocks)) {
        if (entry?.status !== 'blocked') continue;
        if (!matchesMetadata(entry, match)) continue;
        delete nextBlocks[k];
        changed = true;
      }

      return changed ? { ...current, blocks: nextBlocks } : current;
    });
  },

  consume({ companyId, refKey } = {}) {
    const scopeKey = resolveCreditsScopeKey(companyId);
    if (!refKey) return { ok: false, reason: 'invalid' };

    updateCreditsStore(scopeKey, (current) => {
      const summary = computeCreditsSummary(current);
      const existing = summary.blocks[refKey];
      if (!existing) return current;
      if (existing.status !== 'blocked') return current;

      return {
        ...current,
        blocks: {
          ...summary.blocks,
          [refKey]: {
            ...existing,
            status: 'consumed',
            consumedAt: new Date().toISOString(),
          },
        },
      };
    });

    return { ok: true };
  },

  consumeBestEffort({ companyId, refKey, match } = {}) {
    // Try exact refKey first; if missing, consume the first blocked entry matching metadata.
    const scopeKey = resolveCreditsScopeKey(companyId);
    const store = seedCreditsIfEmpty(scopeKey, { total: 20 });
    const summary = computeCreditsSummary(store);

    if (refKey && summary.blocks[refKey]?.status === 'blocked') {
      return this.consume({ companyId, refKey });
    }

    if (!match) return { ok: false, reason: 'not_found' };

    const candidateKey = Object.keys(summary.blocks).find((k) => {
      const entry = summary.blocks[k];
      return entry?.status === 'blocked' && matchesMetadata(entry, match);
    });

    if (!candidateKey) return { ok: false, reason: 'not_found' };

    return this.consume({ companyId, refKey: candidateKey });
  },

  makeRefKey(args) {
    return makeCreditsRefKey(args);
  },
};

